import React from "react";
import "./ProviderLogo.scss";

/**
 * Box sizes, in step with the places provider marks appear: `xs` 14px (chips,
 * breadcrumbs, pills), `sm` 16px (table rows, dropdown items, inline values),
 * `md` 20px (headers, cards), `lg` 40px (detail headers, integration cards),
 * `xl` 48px (choice cards).
 */
export type ProviderLogoSize = "xs" | "sm" | "md" | "lg" | "xl";

/** What a logo draws: an image URL or an inline SVG mark. */
export type ProviderLogoSource =
  | { src: string; icon?: never }
  | {
      /** An inline (`?react`) SVG mark, for vendors without an image URL. */
      icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
      src?: never;
    };

export type ProviderLogoProps = ProviderLogoSource & {
  size?: ProviderLogoSize;
  /**
   * Accessible name, for when no visible text beside the logo names the
   * provider (e.g. an account name follows it). Without one the logo is
   * decorative.
   */
  label?: string;
  /** Hover name for the provider. Defaults to `label`. */
  title?: string;
  /** Spacing / alignment only — the size comes from `size`. */
  className?: string;
  testId?: string;
};

/**
 * A provider or service mark (GitHub, Slack, Snowflake, …). In dark mode it
 * sits on a light chip, so a dark monochrome mark stays legible without
 * recolouring the provider's identity.
 */
const ProviderLogo: React.FC<ProviderLogoProps> = ({
  src,
  icon: Icon,
  size = "sm",
  label,
  title,
  className,
  testId,
}) => (
  <span
    className={`tlx-provider-logo tlx-provider-logo--${size}${className ? ` ${className}` : ""}`}
    title={title ?? label}
    {...(label ? { role: "img", "aria-label": label } : {})}
    data-test-id={testId}
  >
    {Icon ? (
      <Icon
        className="tlx-provider-logo__img"
        aria-hidden="true"
        focusable="false"
      />
    ) : (
      <img
        className="tlx-provider-logo__img"
        src={src}
        alt=""
        aria-hidden="true"
      />
    )}
  </span>
);

export default ProviderLogo;
